// src/pages/LoginPage.jsx
import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { AuthContext, isWebAuthnGestureDenied } from "../../context/AuthContext";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import { loginApi, setBearerToken, fetchData } from "../../api/client";
import { useTheme } from "@mui/material/styles";

// import logo from "../../assets/logo.png";
// import logowhite from "../../assets/logowhite.png";
import { useBrand } from "../../context/BrandContext";

import SmartWorkspaceInline from "../../components/SmartWorkspaceInline";
import MarketingMeasurementScripts from "../../components/MarketingMeasurementScripts";
import {
  clearLegacyGlobalWorkspaceKeys,
  clearTabWorkspaceDatabase,
  setActiveWorkspaceDatabase,
} from "../../utils/workspaceStorage";
import { usePublicSignupAvailability } from "../../hooks/usePublicSignupAvailability";
import { readStoredLocale, setUiLocale, getGlobalLocalesForUiSelect } from "../../i18n/config";
import { parseUiLocaleFromSearch } from "../../i18n/urlLocale";
import { getLoginUiStrings, getLoginLocaleLabel } from "../../i18n/loginUiStrings";
import { chatPathWithWorkspaceNav, isMobileTeamViewport } from "../../utils/teamNavigation";

const COMPANY_RE = /^[a-zA-Z0-9._-]{2,32}$/; // simples e objetivo
const toSlug = (v) => String(v || "").trim().toLowerCase();

function defaultPostLoginHomePath() {
  return isMobileTeamViewport() ? chatPathWithWorkspaceNav() : "/";
}

/** Junta path (pode já ter query) com qs no formato `?a=1`. */
function joinPathAndQs(path, qs) {
  if (!qs) return path;
  const extra = String(qs).startsWith("?") ? String(qs).slice(1) : String(qs);
  if (!extra) return path;
  return String(path).includes("?") ? `${path}&${extra}` : `${path}?${extra}`;
}

/**
 * Destino após login: com workspace preenchido no formulário, sempre entra no app daquele tenant (?ws=),
 * inclusive JWT partner/platform. Sem workspace no formulário, console plataforma / portal do representante.
 * No mobile, o home padrão é atendimento com barra da Equipe (`/chat?workspaceNav=1`).
 */
function resolvePostLoginRoute(user, redirectPath, normalizedWorkspaceSlug, hasWorkspace) {
  const u = user || {};
  const scope = u.scope || "workspace";
  const qs = normalizedWorkspaceSlug ? `?ws=${encodeURIComponent(normalizedWorkspaceSlug)}` : "";
  const defaultHome = defaultPostLoginHomePath();
  if (hasWorkspace && normalizedWorkspaceSlug) {
    // Se o usuário veio de /partner/* ou /platform/*, sempre entra no app do workspace,
    // não no console/portal (mesmo que redirectPath tenha sido setado por um guard).
    const rp = String(redirectPath || "").trim();
    const safeRp =
      rp.startsWith("/partner") || rp.startsWith("/platform") ? defaultHome : (rp || defaultHome);
    return joinPathAndQs(safeRp, qs);
  }
  if (scope === "platform") return "/platform/xprobase";
  if (scope === "partner") return "/partner/console";
  if (Number(u.master) === 1) return "/platform/xprobase";
  return joinPathAndQs(redirectPath || defaultHome, qs);
}

/** Ativa o schema nesta aba (pin sessionStorage + espelho localStorage). */
function persistWorkspaceDatabaseSlug(slug) {
  const s = String(slug || "").trim();
  if (!s || /^\d+$/.test(s)) return;
  setActiveWorkspaceDatabase(s);
}

/** Limpa seleção de workspace nesta aba (login sem tenant / campo vazio). */
function clearPersistedWorkspaceDatabase() {
  clearTabWorkspaceDatabase();
  try {
    localStorage.removeItem("company");
    localStorage.removeItem("xproWorkspaceDatabase");
  } catch {
    /* ignore */
  }
}

function readLoginLocaleInitial() {
  if (typeof window !== "undefined") {
    const fromUrl = parseUiLocaleFromSearch(window.location.search);
    if (fromUrl) return fromUrl;
  }
  return readStoredLocale() || "pt-BR";
}

const LoginPage = () => {
  const brand = useBrand();

  const [email, setEmail] = useState(localStorage.getItem("savedEmail") || "");
  const [biometricCredentialId, setBiometricCredentialId] = useState(() =>
    typeof localStorage !== "undefined" ? localStorage.getItem("biometricId") || "" : ""
  );
  const savedEmail = useMemo(() => localStorage.getItem("savedEmail") || "", []);
  const firstLoad = useMemo(() => sessionStorage.getItem("firstLoad") || "", []);
  const [password, setPassword] = useState("");
  const [company, setCompany] = useState(() => {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem("company") || "" : "";
    if (raw && /^\d+$/.test(String(raw).trim())) return "";
    return toSlug(raw);
  });
  const [, setCompanyTouched] = useState(false);
  const [, setCompanyError] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [localeChoice, setLocaleChoice] = useState(readLoginLocaleInitial);
  const [footerLocaleOpen, setFooterLocaleOpen] = useState(false);
  /** Safari: readOnly inicial em email/senha evita popup de autofill bloqueando o workspace. */
  const [credentialFieldsReady, setCredentialFieldsReady] = useState(false);

  const unlockCredentialFields = useCallback(() => {
    setCredentialFieldsReady(true);
  }, []);

  const handleCredentialFieldMouseDown = useCallback((e) => {
    if (e.currentTarget?.readOnly) {
      e.currentTarget.readOnly = false;
      unlockCredentialFields();
    }
  }, [unlockCredentialFields]);

  const [biometricSupported, setBiometricSupported] = useState(false);
  const [biometricCheckDone, setBiometricCheckDone] = useState(false);
  const { login, redirectPath, biometricLogin } = useContext(AuthContext);
  const [biometricLoginFailed, setBiometricLoginFailed] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePassword = (password) => password.trim().length > 0;
  const location = useLocation();
  const L = getLoginUiStrings(localeChoice);

  // Refs para foco suave
  const companyRef = useRef(null);
  const emailRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const isValidCompany = (c) => COMPANY_RE.test(String(c || "").trim());

  const handleSetCompany = (c) => {
    const v = toSlug(c);
    setCompany(v);
    setCompanyTouched(true);
    if (!v) {
      clearPersistedWorkspaceDatabase();
      setCompanyError("");
      return;
    }
    if (!isValidCompany(v)) {
      setCompanyError(getLoginUiStrings(localeChoice).smartWorkspaceInvalidHint);
    } else {
      setCompanyError("");
      // Pin da aba + localStorage (evita login com tenant antigo do sessionStorage).
      persistWorkspaceDatabaseSlug(v);
    }
  };

  // Aceita ?c=work (sempre minúsculo), valida e já persiste
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get("c");
    if (query) handleSetCompany(query);
  }, [location]);

  // ?lang= / ?locale= / … — alinha o select com a URL (UrlLocaleSync já aplica setUiLocale)
  useEffect(() => {
    const code = parseUiLocaleFromSearch(location.search);
    if (code) setLocaleChoice(code);
  }, [location.search]);

  // Se não veio por query, garante pin/espelho alinhados ao valor do formulário
  useEffect(() => {
    if (company && isValidCompany(company)) {
      const normalized = toSlug(company);
      persistWorkspaceDatabaseSlug(normalized);
    }
  }, [company]);

  useEffect(() => {
    const syncBiometricId = () => {
      try {
        setBiometricCredentialId(localStorage.getItem("biometricId") || "");
      } catch {
        setBiometricCredentialId("");
      }
    };
    syncBiometricId();
    window.addEventListener("storage", syncBiometricId);
    const onVisible = () => {
      if (document.visibilityState === "visible") syncBiometricId();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("storage", syncBiometricId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  // Garante que a página de login sempre comece com scroll no topo (evita logo cortada)
  useEffect(() => {
    window.scrollTo(0, 0);
    const el = scrollContainerRef.current;
    if (el) el.scrollTop = 0;
  }, []);

  // Quando aparece mensagem de erro, rola para o topo para logo e alerta ficarem visíveis
  useEffect(() => {
    if (error) {
      window.scrollTo(0, 0);
      const el = scrollContainerRef.current;
      if (el) el.scrollTop = 0;
    }
  }, [error]);

  // Biometria (inalterado)
  useEffect(() => {
    const checkBiometricAvailabilityOnce = async () => {
      if (!window.PublicKeyCredential) return;
      if (biometricCheckDone) return;
      setBiometricSupported(true);
      setBiometricCheckDone(true);

      if (biometricCredentialId && firstLoad === "false") {
        try {
          await handleBiometricLogin();
        } catch (err) {
          if (!String(err?.message || "").includes("SecretSessionError")) {
            console.error("Erro inesperado:", err);
          }
        }
      }
    };
    checkBiometricAvailabilityOnce();
  }, [biometricCheckDone, firstLoad, biometricCredentialId, company]);

  const isValidLoginInput = () => {
    const trimmedCompany = String(company || "").trim();
    // 1) identificador opcional: se preenchido, precisa ser válido (workspace)
    if (trimmedCompany && !COMPANY_RE.test(trimmedCompany)) {
      setCompanyTouched(true);
      setCompanyError(getLoginUiStrings(localeChoice).companyFieldInvalidLogin);
      try {
        companyRef.current?.focus?.();
      } catch {}
      return false;
    }
    // 2) email
    if (!validateEmail(email)) {
      setError("Insira um email válido.");
      emailRef.current?.focus?.();
      return false;
    }
    // 3) senha
    if (!validatePassword(password)) {
      setError("Senha obrigatória.");
      return false;
    }
    return true;
  };

  const handleLogin = useCallback(async () => {
    if (!isValidLoginInput()) return;

    const trimmedCompany = String(company || "").trim();
    const hasWorkspace = trimmedCompany && COMPANY_RE.test(trimmedCompany);
    const normalized = hasWorkspace ? toSlug(trimmedCompany) : "";

    if (String(process.env.REACT_APP_DEBUG_LOGIN_FLOW || "").trim() === "1") {
      // eslint-disable-next-line no-console
      console.log("[LOGIN_FLOW_FE]", {
        stage: "submit_password_login",
        hasWorkspace,
        normalized,
        redirectPath,
        companyLocalStorage: (() => {
          try { return localStorage.getItem("company"); } catch { return null; }
        })(),
      });
    }

    if (hasWorkspace) {
      persistWorkspaceDatabaseSlug(normalized);
    } else {
      clearPersistedWorkspaceDatabase();
    }

    setLoading(true);
    try {
      setError(null);
      const response = await loginApi(email, password, {
        skipCompanyHeader: !hasWorkspace,
      });
      const preferredLocale = String(localeChoice || "pt-BR").trim() || "pt-BR";
      setUiLocale(preferredLocale);
      login(response.data.token, response.data.user || {});
      localStorage.setItem("savedEmail", email);

      const u = response.data.user || {};
      const scope = u.scope || "workspace";

      // Portal do representante / console plataforma (sem workspace no formulário):
      // - não registrar biometria (endpoints são do tenant)
      // - limpar seleção de projetos para não anexar ids_projetos e gerar 403
      if (!hasWorkspace && (scope === "partner" || scope === "platform")) {
        try {
          clearLegacyGlobalWorkspaceKeys();
        } catch {
          /* ignore */
        }
      } else if (!biometricCredentialId) {
        // Não chamar WebAuthn aqui: o gesto do clique já expirou após o POST /login,
        // e o login() troca o <Router> — await nisso bloqueava o navigate da 1ª tentativa.
        try {
          sessionStorage.setItem("xproOfferBiometricEnroll", "1");
        } catch {
          /* ignore */
        }
      }

      const dest = resolvePostLoginRoute(u, redirectPath, normalized, hasWorkspace);
      if (String(process.env.REACT_APP_DEBUG_LOGIN_FLOW || "").trim() === "1") {
        // eslint-disable-next-line no-console
        console.log("[LOGIN_FLOW_FE]", { stage: "post_login_route", scope: u?.scope, master: u?.master, dest });
      }
      navigate(dest);
    } catch (err) {
      const msg =
        err?.message ||
        err?.response?.data?.message ||
        (Array.isArray(err?.response?.data?.message) && err.response.data.message[0]) ||
        "Não foi possível entrar. Tente novamente.";
      setError(msg);
      if (err?.code === "errors.auth.workspace_identifier_required") {
        setCompanyTouched(true);
        try {
          companyRef.current?.focus?.();
        } catch {
          /* ignore */
        }
      }
    } finally {
      setLoading(false);
    }
  }, [
    email,
    password,
    company,
    redirectPath,
    localeChoice,
    navigate,
    login,
    biometricCredentialId,
  ]);

  const handleBiometricLogin = async () => {
    if (!biometricCredentialId || biometricLoginFailed) return;
    try {
      setLoading(true);
      sessionStorage.setItem("firstLoad", "true");
      const trimmedBio = String(company || "").trim();
      const hasWorkspaceBio = trimmedBio && COMPANY_RE.test(trimmedBio);
      const normalizedBio = hasWorkspaceBio ? toSlug(trimmedBio) : "";
      if (hasWorkspaceBio) {
        persistWorkspaceDatabaseSlug(normalizedBio);
      } else {
        clearPersistedWorkspaceDatabase();
      }
      const response = await biometricLogin(email);
      if (response?.data?.token) {
        setBearerToken(response.data.token);
        const preferredLocale = String(localeChoice || "pt-BR").trim() || "pt-BR";
        const mergedUser = { ...(response.data.user || {}), locale_code: preferredLocale };
        login(response.data.token, mergedUser);
        try {
          await fetchData("/users/me/locale", "PATCH", { locale_code: preferredLocale });
        } catch (localeErr) {
          console.warn("Falha ao persistir locale no perfil:", localeErr?.message || localeErr);
        }
        const u = mergedUser;
        navigate(resolvePostLoginRoute(u, redirectPath, normalizedBio, hasWorkspaceBio));
      }
    } catch (err) {
      console.error("Erro ao realizar autenticação biométrica:", err);
      setError(
        isWebAuthnGestureDenied(err)
          ? "Não foi possível entrar com biometria. Use e-mail e senha."
          : err?.message || "Não foi possível entrar com biometria. Use e-mail e senha."
      );
      setBiometricLoginFailed(true);
    } finally {
      setLoading(false);
    }
  };

  // Enter global: salva workspace se mudou e avança fluxo
  const handleKeyDown = (event) => {
    if (event.key !== "Enter") return;

    const trimmedCompany = String(company || "").trim();
    if (trimmedCompany && !isValidCompany(trimmedCompany)) {
      event.preventDefault();
      setCompanyTouched(true);
      setCompanyError(getLoginUiStrings(localeChoice).smartWorkspaceInvalidHint);
      try {
        companyRef.current?.focus?.();
      } catch {}
      return;
    }

    const normalized = trimmedCompany ? toSlug(trimmedCompany) : "";
    if (normalized) {
      persistWorkspaceDatabaseSlug(normalized);
    } else {
      clearPersistedWorkspaceDatabase();
    }

    // Se foco está em CompanyField, ao Enter move para e-mail para agilizar
    const active = document.activeElement;
    const isOnCompany =
      active?.getAttribute?.("name") === "workspace" ||
      active?.getAttribute?.("name") === "company" ||
      active?.id?.toLowerCase?.().includes?.("company") ||
      active?.id?.toLowerCase?.().includes?.("workspace");

    if (isOnCompany) {
      event.preventDefault();
      emailRef.current?.focus?.();
      return;
    }

    // Senão, tenta logar (deixa a validação decidir)
    event.preventDefault();
    handleLogin();
  };

  const { loading: signupAvailLoading, available: publicSignupAvailable } = usePublicSignupAvailability();

  const handleLocaleChoice = (next) => {
    const locale = String(next || "").trim() || "pt-BR";
    setLocaleChoice(locale);
    setUiLocale(locale);
  };

  return (
    <Box
      ref={scrollContainerRef}
      display="flex"
      flexDirection="column"
      alignItems="center"
      sx={{
        height: "100vh",
        minHeight: "100vh",
        maxHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #121212, #1e1e1e)"
            : "linear-gradient(135deg, #f5f5f5, #e3e3e3)",
        color: theme.palette.text.primary,
        p: 2,
        pt: 4,
        pb: 4,
        overflowY: "auto",
        boxSizing: "border-box",
      }}
      onKeyDown={handleKeyDown}
    >
      <MarketingMeasurementScripts />
      <Box display="flex" alignItems="center" justifyContent="center" mb={4} flexShrink={0}>
        <img
          src={theme.palette.mode === "dark" ? brand.assets.logo.dark : brand.assets.logo.light}
          alt={brand.name}
          style={{ height: 60 }}
          loading="lazy"
        />
      </Box>

      <Paper
        elevation={6}
        sx={{
          p: 4,
          maxWidth: 420,
          width: "90%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          borderRadius: 3,
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Avatar sx={{ bgcolor: theme.palette.primary.main, mb: 2 }}>
          <LockOutlinedIcon />
        </Avatar>

        <Typography variant="h5" gutterBottom>
          {savedEmail ? L.welcomeBack : L.welcome}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ width: "100%", mb: 2 }} role="alert">
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {L.loginFailed}
            </Typography>
            <Typography variant="body2">{error}</Typography>
          </Alert>
        )}

        <Box width="100%" display="flex" flexDirection="column" gap={1.25}>
          <SmartWorkspaceInline
            allowEmpty
            value={company}
            onSave={(val) => {
              const v = toSlug(val);
              setCompany(v);
              setCompanyTouched(true);
              if (!v) {
                clearPersistedWorkspaceDatabase();
                setCompanyError("");
                return;
              }
              setCompanyError(COMPANY_RE.test(v) ? "" : L.companyFieldInvalidLogin);
              if (COMPANY_RE.test(v)) {
                persistWorkspaceDatabaseSlug(v);
              }
            }}
            onEnterNext={() => { try { emailRef.current?.focus?.(); } catch {} }}
            name="company"
            inputRef={companyRef}
            label={L.workspaceField}
            invalidHelperText={L.smartWorkspaceInvalidHint}
          />

          <Box
            component="form"
            autoComplete="on"
            onSubmit={(e) => {
              e.preventDefault();
              handleLogin();
            }}
            width="100%"
            display="flex"
            flexDirection="column"
            gap={1.25}
          >
          {/* Email */}
          <TextField
            inputRef={emailRef}
            name="email"
            label={L.email}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onFocus={unlockCredentialFields}
            fullWidth
            margin="dense"
            autoComplete="username"
            inputProps={{
              autoComplete: "username",
              readOnly: !credentialFieldsReady,
              onMouseDown: handleCredentialFieldMouseDown,
            }}
            error={!!error && !validateEmail(email)}
            helperText={!!error && !validateEmail(email) ? L.invalidEmail : undefined}
          />

          {/* Senha + esqueceu a senha (grupo compacto) */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, mt: -0.25 }}>
            <TextField
              name="password"
              label={L.password}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={unlockCredentialFields}
              fullWidth
              margin="dense"
              autoComplete="current-password"
              inputProps={{
                autoComplete: "current-password",
                readOnly: !credentialFieldsReady,
                onMouseDown: handleCredentialFieldMouseDown,
              }}
              error={!!error && !password}
              helperText={!!error && !password ? L.requiredPassword : undefined}
            />
            <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end", lineHeight: 1.2 }}>
              <Link
                component={RouterLink}
                to="/forgot-password"
                state={{
                  prefilledEmail: String(email || "").trim(),
                  prefilledCompany: (() => {
                    const t = String(company || "").trim();
                    return t && COMPANY_RE.test(t) ? toSlug(t) : "";
                  })(),
                }}
                variant="body2"
                sx={{
                  fontSize: "0.8125rem",
                  textDecoration: "none",
                  color: "text.secondary",
                  "&:hover": { textDecoration: "underline", color: "primary.main" },
                }}
              >
                {L.forgotPassword}
              </Link>
            </Box>
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={handleLogin}
            fullWidth
            sx={{
              mt: 0.75,
              height: 48,
              fontWeight: "bold",
              fontSize: "1rem",
              textTransform: "none",
            }}
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} color="inherit" />}
          >
            {loading ? L.loggingIn : L.login}
          </Button>

          {biometricSupported && !biometricLoginFailed && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                if (!biometricCredentialId) {
                  setError(L.biometricRequiresPasswordFirst);
                  return;
                }
                handleBiometricLogin();
              }}
              fullWidth
              disabled={loading}
              sx={{
                mt: 1.25,
                height: 48,
                fontWeight: 700,
                fontSize: "0.8125rem",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                borderWidth: 2,
                "&:hover": { borderWidth: 2 },
              }}
            >
              {L.biometric}
            </Button>
          )}

          {!signupAvailLoading && publicSignupAvailable && (
            <Box sx={{ width: "100%", textAlign: "center", mt: 1.25 }}>
              <Typography
                component="span"
                variant="body2"
                sx={{ fontSize: "0.8125rem", color: "text.secondary" }}
              >
                {L.noAccount}{" "}
              </Typography>
              <Link
                component={RouterLink}
                to="/signup"
                variant="body2"
                sx={{ fontSize: "0.8125rem", fontWeight: 500 }}
              >
                {L.createWorkspaceLink}
              </Link>
            </Box>
          )}
          </Box>
        </Box>
      </Paper>

      <Box
        mt={4}
        pb={2}
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={0.75}
        sx={{ flexShrink: 0 }}
      >
        {footerLocaleOpen ? (
          <FormControl size="small" variant="standard" sx={{ minWidth: 200 }}>
            <Select
              open={footerLocaleOpen}
              onClose={() => setFooterLocaleOpen(false)}
              value={localeChoice}
              onChange={(e) => {
                handleLocaleChoice(e.target.value);
                setFooterLocaleOpen(false);
              }}
              autoFocus
              MenuProps={{
                anchorOrigin: { vertical: "top", horizontal: "center" },
                transformOrigin: { vertical: "bottom", horizontal: "center" },
              }}
              sx={{
                fontSize: "0.8125rem",
                color: theme.palette.text.secondary,
                "&::before, &::after": { display: "none" },
                "& .MuiSelect-select": { py: 0.25, pr: "24px !important" },
              }}
            >
              {getGlobalLocalesForUiSelect().map(({ code, nativeName }) => (
                <MenuItem key={code} value={code}>
                  {nativeName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Typography
            component="button"
            type="button"
            variant="caption"
            onClick={() => setFooterLocaleOpen(true)}
            aria-label={L.localeLabel}
            aria-haspopup="listbox"
            sx={{
              cursor: "pointer",
              color: theme.palette.text.secondary,
              border: 0,
              background: "none",
              font: "inherit",
              p: 0,
              textDecoration: "underline",
              textDecorationColor: theme.palette.action.disabled,
              textUnderlineOffset: 3,
              "&:hover": {
                color: theme.palette.primary.main,
                textDecorationColor: theme.palette.primary.main,
              },
            }}
          >
            {getLoginLocaleLabel(localeChoice)}
          </Typography>
        )}
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: "center" }}>
          © {new Date().getFullYear()} {brand.name}. Todos os direitos reservados.
        </Typography>
      </Box>
    </Box>
  );
};

export default LoginPage;
